import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { userService } from "../services/userService";
import { useAuth } from "../contexts/AuthContext";
import { isDirectGarminDevice } from "../services/garminService";

const DATA_TYPES = [
  { key: "sleep", label: "Sleep" },
  { key: "activity", label: "Activity" },
  { key: "daily", label: "Daily" },
  { key: "menstruation", label: "Menstruation" },
];

interface TerraDevice {
  id: string;
  resource: string;
  connectedAt: string;
  expired?: boolean;
  integration?: string;
}

interface BackfillResult {
  deviceId: string;
  resource: string;
  type: string;
  status: "triggered" | "skipped" | "error";
  message?: string;
}

interface BackfillResponse {
  success: boolean;
  message: string;
  dateRange: { startDate: string; endDate: string; days: number };
  results: BackfillResult[];
}

export default function TerraBackfill() {
  const { currentUser } = useAuth();
  const [devices, setDevices] = useState<TerraDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([
    "sleep",
    "activity",
  ]);
  const [days, setDays] = useState<number>(7);
  const [loading, setLoading] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [response, setResponse] = useState<BackfillResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDevices = async () => {
      if (!currentUser?.uid) return;
      try {
        setLoadingDevices(true);
        const { userData } = await userService.getUserProfile(currentUser.uid);
        const activeDevices = (
          (userData?.deviceIDs as TerraDevice[]) ?? []
        ).filter((d) => !d.expired && d.resource !== "STRAVA");
        setDevices(activeDevices);
        if (activeDevices.length === 1) {
          setSelectedDeviceId(activeDevices[0].id);
        }
      } catch (err) {
        setError("Failed to load connected devices.");
      } finally {
        setLoadingDevices(false);
      }
    };
    fetchDevices();
  }, [currentUser?.uid]);

  const toggleType = (key: string) => {
    setSelectedTypes((prev) =>
      prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key],
    );
  };

  const formatDeviceName = (resource: string) =>
    resource.charAt(0) + resource.slice(1).toLowerCase();

  const handleBackfill = async () => {
    if (!currentUser) return;
    if (!selectedDeviceId) {
      setError("Please select a device.");
      return;
    }
    if (selectedTypes.length === 0) {
      setError("Select at least one data type.");
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    const selectedDevice = devices.find((d) => d.id === selectedDeviceId);
    const isGarmin = !!selectedDevice && isDirectGarminDevice(selectedDevice);

    try {
      const functions = getFunctions();

      // Garmin connects direct, not via Terra — it has no record of this
      // device, so routing it through terraBackfill would just error.
      if (isGarmin) {
        const garminBackfill = httpsCallable<
          Record<string, never>,
          { success: boolean; message: string }
        >(functions, "garminBackfill");
        const result = await garminBackfill({});
        setResponse({
          success: result.data.success,
          message: result.data.message,
          dateRange: { startDate: "", endDate: "", days },
          results: [],
        });
        return;
      }

      const terraBackfill = httpsCallable<
        { deviceId: string; types: string[]; days: number },
        BackfillResponse
      >(functions, "terraBackfill");

      const result = await terraBackfill({
        deviceId: selectedDeviceId,
        types: selectedTypes,
        days,
      });

      setResponse(result.data);
    } catch (err: any) {
      setError(err?.message ?? "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className=" space-y-5 w-full">
      <div>
        <h3 className="font-semibold text-base text-base-content">
          Sync historical data
        </h3>
        <p className="text-sm text-base-content/60 mt-0.5">
          Pull past wearable data from a connected device.
        </p>
      </div>

      {/* Device selector */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-base-content/50 uppercase tracking-wide">
          Device
        </p>

        {loadingDevices ? (
          <div className="flex items-center gap-2 py-2">
            <span className="loading loading-spinner loading-xs text-base-content/40" />
            <span className="text-sm text-base-content/40">
              Loading devices...
            </span>
          </div>
        ) : devices.length === 0 ? (
          <p className="text-sm text-base-content/50 py-1">
            No active devices connected.
          </p>
        ) : (
          <div className="space-y-2">
            {devices.map((device) => (
              <label
                key={device.id}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                  selectedDeviceId === device.id
                    ? "border-neutral bg-base-200"
                    : "border-base-300 bg-base-100 hover:bg-base-200/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="device"
                    value={device.id}
                    checked={selectedDeviceId === device.id}
                    onChange={() => setSelectedDeviceId(device.id)}
                    className="radio radio-sm radio-neutral"
                  />
                  <div>
                    <p className="text-sm font-medium text-base-content">
                      {formatDeviceName(device.resource)}
                    </p>
                    <p className="text-xs text-base-content/40">
                      Connected{" "}
                      {new Date(device.connectedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-700 dark:text-green-400">
                  Active
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Data type toggles */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-base-content/50 uppercase tracking-wide">
          Data types
        </p>
        <div className="flex flex-wrap gap-2">
          {DATA_TYPES.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => toggleType(key)}
              className={`btn-sm rounded-md border transition-all flex items-center ${
                selectedTypes.includes(key)
                  ? ""
                  : " border-base-300 text-base-content/30"
              }`}
            >
              {selectedTypes.includes(key) && (
                <svg
                  className="w-3.5 h-3.5 mr-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Days slider */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <p className="text-xs font-medium text-base-content/50 uppercase tracking-wide">
            Days to look back
          </p>
          <span className="text-sm font-semibold text-base-content">
            {days}d
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={28}
          step={1}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="range range-sm range-neutral w-full"
        />
        <div className="flex justify-between text-xs text-base-content/40">
          <span>1 day</span>
          <span>28 days</span>
        </div>
      </div>

      {/* CTA */}
      <button
        onClick={handleBackfill}
        disabled={
          loading ||
          selectedTypes.length === 0 ||
          !selectedDeviceId ||
          devices.length === 0
        }
        className="bg-base-200 rounded-md p-2 font-bold w-full"
      >
        {loading ? (
          <span className="loading loading-spinner loading-xs" />
        ) : null}
        {loading ? "Requesting..." : "Sync now"}
      </button>

      {/* Error */}
      {error && (
        <div className="alert alert-error py-2 px-3 text-sm">
          <span>{error}</span>
        </div>
      )}

      {/* Success response */}
      {response && (
        <div className="space-y-3">
          <div
            className={`alert py-2 px-3 text-sm ${response.success ? "alert-success" : "alert-warning"}`}
          >
            <span>{response.message}</span>
          </div>

          <p className="text-xs text-base-content/50">
            Range: {response.dateRange.startDate} → {response.dateRange.endDate}
          </p>

          <div className="space-y-1">
            {response.results.map((r, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-base-200"
              >
                <span className="text-base-content/70">
                  {r.resource} · {r.type}
                </span>
                <span
                  className={`font-medium ${
                    r.status === "triggered"
                      ? "text-success"
                      : r.status === "error"
                        ? "text-error"
                        : "text-base-content/40"
                  }`}
                >
                  {r.status}
                </span>
              </div>
            ))}
          </div>

          <p className="text-xs text-base-content/40">
            Data will appear shortly as your devices sync in the background.
          </p>
        </div>
      )}
    </div>
  );
}
